import type { NamespaceSharedTranslation } from '../../i18n-types';

const de_shared = {
  accessGuard: {
    confirmText: 'Zugriff erhalten',
    error: {
      gainAccess: 'Fehler beim Zugreifen',
    },
    inputPlaceholder: 'Warum benötigen Sie Zugriff auf diesen Patienten?',
    message:
      'Sie haben keinen Zugriff auf diesen Patienten. Sie können temporären Zugriff für 24 Stunden erhalten.',
    rejectText: 'Zurück',
    title: 'Kein Zugriff',
  },
  forbiddenPatient: {
    title: 'Kein Zugriff auf diese Patientin oder diesen Patienten',
    body: 'Sind Sie für mehrere Betriebsstätten tätig? Achten Sie bitte darauf, mit der richtigen angemeldet zu sein.',
    nspVerification:
      'Ihr Zugriff setzt eine bestätigte Behandlungsbeziehung voraus. Diese besteht derzeit nicht.',
    contactSupport:
      'Wenn Sie Zugriff benötigen, wenden Sie sich an den Support.',
    supportPhoneLabel: 'Tel.:',
    contactSupportButton: 'Support kontaktieren',
    goBack: 'Zurück',
  },
  alertMessages: {
    anErrorOccurred: 'Ein Fehler ist aufgetreten',
    cantBeUndone: 'Diese Aktion kann nicht rückgängig gemacht werden.',
    comingSoon: 'Demnächst verfügbar!',
    confirmDelete: 'Sind Sie sicher, dass Sie dies löschen möchten?',
    confirmDeleteLesion:
      'Möchten Sie diese Läsion wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    confirmDeleteRash:
      'Möchten Sie diesen Ausschlag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    confirmDeleteDraft:
      'Sind Sie sicher, dass Sie diesen Entwurf löschen möchten? Frühere Einträge zu diesem Fall bleiben erhalten.',
    deleteLesion: 'Läsion löschen?',
    deleteRash: 'Ausschlag löschen?',
    deleteDraft: 'Entwurf löschen?',
    deletionError: 'Löschung fehlgeschlagen',
    deletionSuccess: 'Löschung erfolgreich',
    imageNotSaved: `{imagetype}-Bild wurde aufgrund eines Fehlers nicht gespeichert. Bitte fügen Sie bei Bedarf ein neues Bild hinzu.`,
    theLesionIsEmpty: 'Die Läsion erhält keine Informationen',
    theRashIsEmpty: 'Der Ausschlag erhält keine Informationen',
    wouldYouLikeToDelete: 'Soll der Eintrag gelöscht werden?',
  },
  assessmentTypes: {
    histopathologicalEvaluation: 'Histopathologische Einschätzung',
    teleEvaluation: 'Dermatologische Einschätzung',
  },
  authentication: {
    mitid: {
      login: 'Mit MitID einloggen',
      newUser: 'Neuer Benutzer',
      notYet: 'Noch nicht',
      title: 'MitID in Dermloop',
      errorTitle: 'Falsche MitID',
      signinWithOtherAcc: 'Mit anderer MitID anmelden',
      wrongMitId:
        'Sie haben sich mit einer falschen MitID angemeldet, die nicht zur CVR-Nummer Ihrer Einrichtung passt.',
      subtitle: 'Wir sind gerade dabei, MitID in Dermloop zu integrieren',
      oneLoginSubtitle: 'Sie sind jetzt über Ihr EHR-System angemeldet.',
      oneLoginConnectAccounts:
        'Um fortzufahren, verbinden Sie sich bitte mit der MitID Erhverv, die mit der Klinik verknüpft ist, in der Sie arbeiten. Dieser Schritt stellt sicher, dass wir Ihr Konto korrekt zuordnen können.',
      connectAccounts:
        'Daher bitten wir Sie, Ihren Dermloop-Account mit MitID zu verbinden. Wir entschuldigen uns für die Unannehmlichkeiten.',
      notYetConnected:
        'Sie haben Ihr Dermloop-Account noch nicht mit MitID verbunden. Bitte melden Sie sich wie gewohnt an und folgen Sie den Migrationsschritten, bevor Sie sich mit MitID anmelden.',
      goBack: 'Zurück',
      migrate: '',
      migratingUser: '{user}',
    },
    desktopQrLogin: {
      errorMessage:
        'Beim Anmelden ist ein Problem aufgetreten. Falls dieses Problem weiterhin besteht, kontaktieren Sie bitte den Support: +45 44 40 82 45',
      alreadyRedeemedErrorMessage:
        'Dieser Code wurde bereits für eine Anmeldung verwendet. Falls Sie dies nicht selbst waren, wenden Sie sich bitte umgehend an den Support: +45 44 40 82 45',
    },
    errors: {
      fetchingUser: 'Fehler beim Abrufen des Benutzers aufgetreten',
      internalServer:
        'Interner Serverfehler. Bitte versuchen Sie es später erneut.',
      websocketConnection: 'Keine Verbindung zum WebSocket-Server',
    },
    inputs: {
      email: {
        label: 'E-Mail',
      },
      firstName: {
        label: 'Vorname',
      },
      lastName: {
        label: 'Nachname',
      },
      missingValueMessage: 'Bitte geben Sie Ihren {label} ein',
      password: {
        label: 'Passwort',
      },
      phonenumber: {
        label: 'Telefon',
        validation: {
          invalidForPrefix:
            'Telefonnummer stimmt nicht mit der Landesvorwahl überein',
        },
      },
      termsAndConditions: {
        label: 'E-Mail',
      },
    },
    screens: {
      invalidLoginMethod: {
        title: 'Ungültige Anmeldemethode',
        description:
          'Die von Ihnen verwendete Anmeldemethode ist für diesen Account nicht zulässig. Bitte verwenden Sie eine andere Anmeldemethode oder kontaktieren Sie den Support.',
        button: 'Zurück zur Anmeldung',
      },
      approveDeviceSession: {
        description:
          'Bestätigen Sie den Zugriff auf das Gerät, indem Sie den Code drücken, der mit dem Code auf dem Gerät übereinstimmen, mit dem Sie sich authentifizieren.',
        errorResult: {
          text: 'Fehler bei der Authentifizierung der Gerätesitzung. Bitte scannen Sie den QR-Code erneut.',
          title: 'Gerät nicht authentifiziert',
        },
        errors: {
          gettingDeviceSession: 'Fehler beim Abrufen der Gerätesitzung',
          missingAuthCode: 'Kein Code in den URL-Parametern angegeben',
          missingSessionId: 'Keine Sitzungs-ID in den URL-Parametern angegeben',
        },
        successResult: {
          text: 'Sie können dieses Fenster jetzt schließen.',
          title: 'Gerät authentifiziert',
        },
        title: 'Gerät authentifizieren',
      },
      changePassword: {
        inputs: {
          confirmNewPassword: {
            label: 'Neues Passwort bestätigen',
            validation: {
              passwordMismatch: 'Passwörter stimmen nicht überein',
            },
          },
          newPassword: {
            label: 'Neues Passwort',
          },
        },
        reasons: {
          newLoginSystem:
            'Dermloop wechselt zu einem neuen Anmeldesystem. Bitte erstellen Sie daher ein neues Passwort.',
        },
        submitText: 'Passwort ändern',
        successResult: {
          recoveryModeOnlyText: 'Sie können dieses Fenster jetzt schließen',
          text: 'Sie können nun auf die App zugreifen, indem Sie auf die Schaltfläche unten klicken.',
          title: 'Ihr Passwort wurde erfolgreich geändert.',
        },
        title: 'Passwort ändern',
        titleSucess: 'Passwort erfolgreich geändert',
      },
      completeProfile: {
        title: 'Profil vervollständigen',
        firstName: 'Vorname',
        firstNamePlaceholder: 'Max',
        lastName: 'Nachname',
        lastNamePlaceholder: 'Mustermann',
        email: 'E-Mail - Damit wir Sie kontaktieren können',
        emailPlaceholder: 'max.mustermann@domain.com',
        continueButton: 'Weiter',
        helpUsText:
          'Vielen Dank, dass Sie uns helfen, Ihre Informationen zu vervollständigen',
        errors: {
          thisIsRequired: 'Pflichtfeld',
          errorUpdatingUser:
            'Beim Aktualisieren des Benutzers ist ein Fehler aufgetreten.',
        },
      },
      createAccount: {
        existingAccountLinkText: 'Einloggen',
        existingAccountText: 'Haben Sie bereits einen Account?',
        privacyPolicyLink: 'https://melatech.io/legal/privacy',
        privacyPolicyLinkText: 'Datenschutzrichtlinie.',
        readTermsAndConditionsText: 'Ich habe die',
        submitText: 'Registrieren',
        subtitle:
          'Erstellen Sie einen Account, um auf unsere Fallbibliothek und vieles mehr zuzugreifen.',
        termsAndConditionsLink: 'https://melatech.io/legal/terms',
        termsAndConditionsLinkText: 'Nutzungsbedingungen',
        termsAndConditionsSeperator: 'und',
        title: 'Account erstellen',
      },

      login: {
        createAccount: 'Account erstellen',
        creatingQRCode: 'QR-Code wird erstellt',
        dermKit: 'Dermkit',
        errors: {
          errorOccuredWhileCreatingQRCode: 'Fehler beim Erstellen des QR-Codes',
          incorrectEmailOrPassword:
            'Falsche E-Mail-Adresse oder falsches Passwort.',
          qrCodeExpired: 'QR-Code abgelaufen',
        },
        forgotPassword: 'Passwort vergessen?',
        login: 'Einloggen',
        loginError: 'Anmeldung fehlgeschlagen',
        loginWithEmail: 'Zu E-Mail wechseln',
        loginWithGoogle: 'Mit Google einloggen',
        loginWithQRCode: 'Mit QR-Code einloggen',
        needAnAccount: 'Neu bei Dermloop?',
        personalDevice: 'Persönliches Gerät',
        qrCodeCountDown: 'Aktiv für {time} min',
        resetQRCode: 'QR-Code zurücksetzen',
        scanQRCode: 'Mit QR-Code einloggen',
        subtitle: 'Willkommen zurück',
      },

      medicalUnitInvitation: {
        accept: 'Akzeptieren',
        additionalRoles: 'Zusätzliche Rollen',
        invitedToMedicalUnit:
          'Sie wurden eingeladen, einem Standort beizutreten',
        reject: 'Ablehnen',
      },

      mfaConfirmation: {
        infoPopUp: {
          paragraph1:
            'Sie können sich ohne SMS-Verifizierung auf einem authentifizierten Gerät anmelden.',
          paragraph2:
            'Die Authentifizierung ist 30 Tage gültig und muss danach erneuert werden.',
          title: 'Dieses Gerät authentifizieren',
        },
        inputs: {
          approveDevice: {
            label: 'Dieses Gerät für 30 Tage merken',
          },
          verificationCode: {
            label: 'Verifizierungscode',
            validation: {
              invalidCode: 'Ungültiger Code',
            },
          },
        },
        paragraph1: 'Bitte öffnen Sie Ihr Mobiltelefon',
        paragraph2: 'und geben Sie den per SMS zugesandten Code ein:',
        resendText: 'Neue SMS senden',
        submitText: 'Code bestätigen',
        editText: 'Telefonnummer bearbeiten',
        title: 'Identität bestätigen',
        noSmsTitle: 'Keine SMS mit Verifizierungscode erhalten?',
        noSmsAction:
          'Überprüfen Sie die Telefonnummer und versuchen Sie anschließend, die SMS erneut zu senden.',
      },

      mfaEnrollment: {
        paragraph1: 'Wir müssen Ihre Identität verifizieren.',
        paragraph2:
          'Wir senden einen 6-stelligen Code an Ihre Telefonnummer zur Verifizierung.',
        submitText: 'Telefonnummer bestätigen',
        title: 'Telefonnummer hinzufügen',
      },

      pickMedicalUnit: {
        subtitle:
          'Bitte wählen Sie den Standort aus, auf die Sie zugreifen möchten',
        successMessage: 'Zum Standort {medicalUnitName} gewechselt',
        noAccessMessage:
          'Sie haben mit dem Account {username} keinen Zugriff auf einen Standort. Bitte kontaktieren Sie Ihren lokalen Superuser oder schreiben Sie an Melatech Support unter support@melatech.io',
        title: 'Standort auswählen',
      },
      recovery: {
        checkYourMail:
          'Bitte prüfen Sie Ihr Postfach und geben Sie den Code ein, den wir Ihnen an',
        emailInstructions:
          'Geben Sie Ihre E-Mail-Adresse ein, und wir senden Ihnen Anweisungen zum Zurücksetzen Ihres Passworts.',
        submitCheckCode: 'Identität bestätigen',
        submitEmail: 'Wiederherstellungscode senden',
        title: 'Account wiederherstellen',
      },
    },
    slowLoadingText: 'Das dauert länger als gewöhnlich.',
    signingOut: 'Abmeldung läuft…',
    logout: 'Abmelden',
  },
  dataTable: {
    filters: 'Filter',
    clearAllFilters: 'Alle löschen',
    noActiveFilters: 'Keine aktiven Filter',
    removeFilter: 'Filter entfernen – {category}:',
    searchOptions: 'Suchen',
    noMatchingOptions: 'Keine Treffer',
  },
  buttons: {
    add: '+',
    back: 'Zurück',
    cancel: 'Abbrechen',
    changeMedicalUnit: 'Standort wechseln',
    clear: 'Löschen',
    close: 'Schließen',
    collapse: 'Einklappen',
    completeEntry: 'eKonsil abschließen',
    confirm: 'Bestätigen',
    continue: 'Weiter',
    delete: 'Löschen',
    deleteAccount: 'Account löschen',
    edit: 'Bearbeiten',
    filter: 'Filtern',
    help: 'Hilfe',
    leave: 'Verlassen',
    patientOverview: 'Patientenübersicht',
    remove: 'Entfernen',
    reset: 'Zurücksetzen',
    restore: 'Wiederherstellen',
    save: 'Speichern',
    saveAndContinue: 'Speichern und fortfahren',
    saveChanges: 'Änderungen speichern',
    saveDraft: 'Entwurf speichern',
    saveDraftDisabled:
      'Nur die Person, die den Entwurf erstellt hat, kann diesen speichern oder löschen. Verwenden Sie den grünen Button, um den Fall abzuschließen.',
    saveDraftDisabledAssistant:
      'Nur die Person, die den Entwurf erstellt hat, kann diesen speichern oder löschen.',
    send: 'Senden',
    sendPathology: 'An Pathologen senden',
    sendTeleReferral: 'eKonsil einreichen',
    signIn: 'Einloggen',
    signOut: 'Ausloggen',
    read: 'Lesen',
    skip: 'Überspringen',
    tryAgain: 'Erneut versuchen',
  },
  clinicalActions: {
    biopsy: 'Biopsie',
    biopsyAction: 'Biopsietyp',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Kürettage, Excision: Exzision, Incision: Inzision, Punch: Punch, Shave: Shave, *: Nicht spezifiziert}} {text}',
    clinicalAction: 'Klinische Handlung',
    dismissal: 'Keine weitere Handlung',
    followUpAssessment: 'Verlaufsuntersuchung',
    newImageRegistration: 'Neue Bildaufnahme',
    noAction: 'Keine Handlung',
    notSpecified: 'Nicht spezifiziert',
    physical: 'Physisch',
    plannedBiopsy: 'Geplante Biopsie',
    referral: 'Überweisung',
    referralActionText:
      '{referralType|{physical: Physische Überweisung, *: eKonsil}}',
    tele: 'Tele',
    topicalTreatment: 'Topische Behandlung',
    noTreatment: 'Keine Behandlung',
    scraping: 'Abkratzen / Abstrich',
    cryotherapy: 'Kryotherapie',
    oralTreatment: 'Orale Behandlung',
    other: 'Andere',
  },
  dateDayMonthFormat: 'dd.MM',
  dateFormat: 'dd.MM.yyyy',
  dateHoursFormat: 'dd.MM.yyyy HH:mm',
  hoursFormat: 'HH:mm',
  dateText: {
    daysFromBaseline: '{dateDiff} Tag{{e}} seit Ausgangspunkt',
    followUpMonths: '{months} Monat{{e}}',
    monthsFromBaseline: '{dateDiff} Monat{{e}} seit Ausgangspunkt',
  },
  deleteUserRequest: {
    actionText: {
      cancel: 'Abbrechen',
      close: 'Schließen',
      delete: 'Löschen',
    },
    impactNote:
      'Beachten Sie, dass dadurch Ihr Account in <>Dermloop Learn<>, <>Dermloop Desktop<> und <>Dermloop Capture<> gelöscht wird.',
    processNote:
      'Die Accountlöschung wird manuell vom Melatech-Team innerhalb eines Monats durchgeführt.',
    regretInstruction:
      'Falls Sie die Löschung Ihres Accounts rückgängig machen möchten, melden Sie sich vor dem Löschdatum erneut an. Sie werden dann gefragt, ob Sie die Löschung widerrufen möchten.',
    requestPrompt: 'Möchten Sie Ihren Account löschen?',
    requestRegistered:
      'Wir haben Ihren Antrag auf Accountlöschung registriert. Ihr Account wird <>innerhalb eines Monats<> gelöscht.',
    title: {
      received: 'Antrag erhalten',
      requested: 'Account löschen?',
    },
  },
  skinCheckRecency: {
    lessThanOneYear: 'Weniger als 1 Jahr',
    oneToFiveYears: '1-5 Jahre',
    moreThanFiveYears: 'Mehr als 5 Jahre',
    never: 'Nie',
    unknown: 'Weiß nicht',
  },
  descriptors: {
    action: 'Aktion',
    addDifferentialDiagnosis: 'Differenzialdiagnose hinzufügen',
    ageOfLesion: 'Wann ist die Läsion aufgetreten?',
    ageOfRash: 'Wann ist der Ausschlag aufgetreten?',
    lastSkinCheck:
      'Wann wurde Ihre Haut zuletzt von einem Arzt oder Hautarzt untersucht?',
    plannedSkinCheckWithinSixMonths:
      'Hatten Sie vor, Ihre Haut innerhalb der nächsten 6 Monate von einem Arzt oder Hautarzt untersuchen zu lassen?',
    allLesions: 'Alle Läsionen',
    allConditions: 'Alle Befunde',
    allUrgencies: 'Alle Dringlichkeiten',
    archive: 'Archiv',
    assessedBy: 'Beurteilt von',
    changesInAppearance: 'Veränderungen im Erscheinungsbild',
    clinicalDiagnosis: 'Diagnose',
    clinicalDiagnosisFromLesion: 'Was ist Ihre Verdachtsdiagnose?',
    clinicalDiagnosisFromRash: 'Was ist Ihre Verdachtsdiagnose?',
    dateOfBirth: 'Geburtsdatum',
    describeOther: '"Andere" beschreiben',
    describeTopicalTreatment: 'Topische Behandlung beschreiben',
    differentialDiagnosis: 'Differenzialdiagnose',
    dob: '*',
    enterImmunosuppressantAndDate:
      'Immunsuppressivum und Start-/Enddatum eingeben',
    enterMedication:
      'Dauer, Dosierung und Häufigkeit von Medikament/Salbe eingeben',
    enterTreatment: 'Frühere Behandlung/Befund und Ergebnis eingeben',
    excisionSize: 'Exzisionsgröße',
    familyHistoryOfMelanoma: 'Familienanamnese für Melanom',
    fetchingPatient: 'Aktuellste Patientendaten werden abgerufen',
    formerSkinCancer: 'Frühere Hautkrebserkrankung',
    formerTreatments: 'Frühere Behandlungen',
    formerTreatmentsFromLesion:
      'Wurde die Läsion bereits befundet oder behandelt?',
    formerTreatmentsFromRash:
      'Wurde der Ausschlag bereits behandelt? (z. B. topische oder orale Steroide, Antihistaminika etc.)',
    formerTreatmentsOrTraumas: 'Frühere Behandlung{{nr:en}}',
    fromLesion: 'Von der Läsion',
    hasAggravatingFactors:
      'Gibt es Faktoren, die den Ausschlag verschlimmern? (Kälte, Stress, etc.)',
    hasAlleviatingFactors:
      'Gibt es Faktoren, die den Ausschlag lindern? (Sonneneinstrahlung, Feuchtigkeitspflege, etc.)',
    hasGeneralSymptoms:
      'Treten neue systemische Symptome mit dem Ausschlag auf? (Fieber, Gelenkschmerzen, etc.)',
    hasMedicalOrExposure:
      'Wurden vor dem Auftreten des Ausschlags neue Medikamente eingenommen oder gibt es potenzielle Auslöser? (Hautpflege/Kosmetika, Seife, Waschmittel, etc.)',
    histologicalDiagnosis: 'Histologische Diagnose',
    histopathologicalEvaluation: 'Histopathologische Untersuchung',
    image: 'Bild',
    images: 'Bild(er)',
    immunosuppressantUse: 'Aktuelle/frühere Einnahme von Immunsuppressiva?',
    immunosuppressantUseExample:
      '(z. B. nach Organ- oder Knochenmarktransplantation)',
    inspection: 'ABCD-Kriterien',
    lastAccessed: 'Zuletzt aufgerufen',
    lastClinicalAction: 'Letzte klinische Handlung',
    lastRegistration: 'Letztes eKonsil',
    condition: 'Befund',
    lesionDoesntMatch: 'Läsion passt nicht zum Patienten',
    lesionImageBaseline: 'Ausgangspunkt',
    conditions: 'Befunde',
    lesionThickness: 'Geschätzte Dicke',
    loading: 'Wird geladen',
    malignancy: 'Malignität',
    medicalRecord: 'Patientenakte',
    noChanges: 'Keine Veränderungen',
    noLesion: 'Keine Läsion',
    noPatient: 'Kein Patient',
    noSymptoms: 'Keine Symptome',
    note: 'Weitere Informationen',
    eConsultNote: 'Weitere Informationen',
    caseSummaryNote: 'Weitere Informationen',
    nrImages: '{nr} Bild{{er}}',
    nrMonths: '{nr} Monat{{e}}',
    objectiveFindings: 'Objektive Befunde',
    optional: 'Optional',
    other: 'Andere',
    otherAge: 'Anderes Alter',
    otherAggravatingFactors: 'Andere verschlimmernde Faktoren eingeben',
    otherAlleviatingFactors: 'Andere lindernde Faktoren eingeben',
    otherAppearanceChanges: 'Andere Erscheinungsveränderung(en)',
    otherDiscomforts: 'Weitere Beschwerden',
    otherInspections: 'Weitere Kriterien',
    otherMedicationOrExposure:
      'Neue Medikamente und/oder Expositionen mit Dauer eingeben',
    otherRashAge: 'Andere Dauer des Ausschlages',
    otherSymptoms: 'Andere Symptome',
    otherTreatmentsFromLesion:
      'Vorherigen Befund/Behandlung und Ergebnis eingeben',
    otherTreatmentsOrTrauma: 'Andere Behandlung oder Trauma',
    pathologySpecimenID: 'Proben-ID',
    patientInfo: 'Patienteninformation',
    pregnancyWithin6Months: 'Schwangerschaft innerhalb der letzten 6 Monate',
    rateDiagnosticsDifficulty: 'Diagnostische Schwierigkeit bewerten',
    reasonForConsult: 'Fragestellung des eKonsils',
    recentChanges:
      'Hat sich die Läsion in Größe, Form, Farbe oder Textur verändert?',
    referrer: 'Überweiser',
    reportedByPatient: 'Vom Patienten berichtet',
    rescanPathologyID: 'Proben-ID erneut scannen',
    restoreAccount: 'Account wiederherstellen?',
    riskFactors: 'Risikofaktoren',
    routine: 'Routine',
    scanSpecimentIDNumber: 'Proben-ID scannen',
    selectAggravatingFactors:
      'Faktoren auswählen, die den Ausschlag verschlimmern',
    selectAlleviatingFactors: 'Faktoren auswählen, die den Ausschlag lindern',
    selectChanges: 'Veränderung(en) auswählen',
    selectObjectiveFindings: 'Objektiven Befund auswählen',
    selectRelevant: 'Relevantes auswählen',
    selectRelevantChanges: 'Relevante Veränderungen auswählen',
    selectRiskFactors: 'Risikofaktor(en) auswählen',
    selectSkinTypeMatch: 'Hauttyp auswählen',
    selectSymptoms: 'Symptom(e) auswählen',
    sinceWhen: 'Seit wann?',
    skinType: 'Hauttyp',
    SSN: 'CPR',
    symptom: 'Symptom',
    symptoms: 'Symptome',
    symptomsFromLesion:
      'Versursacht die Läsion Symptome? (Juckreiz, Schmerzen, Blutung, etc.)',
    symptomsFromRash:
      'Verursacht der Ausschlag Juckreiz, Schmerzen oder andere Beschwerden?',
    teleEvaluation: 'Dermatologische Einschätzung',
    tentativeClinicalDiagnosis: 'Klinische Verdachtsdiagnose',
    tentativeDiagnosis: 'Verdachtsdiagnose',
    typeOfCancer: 'Tumortyp',
    typeOfTeledermatology: 'Dermatologie-Tpy',
    typeOfTreatmentOrTrauma: 'Behandlungs-Typ',
    uniqueIdentificationNumber: '{uniqueIdentificationNumber}',
    urgent: 'Dringend',
    whenChangesBegin: 'Wann begannen die Veränderungen an der Läsion?',
    yesSelectChanges: 'Ja – Veränderungen auswählen',
    yesSelectSymptoms: 'Ja – Symptome auswählen',
    yo: 'Jahre alt',
  },
  diagnosis: {
    carcinoma: 'Karzinom',
    melanoma: 'Melanom',
  },
  errorMessages: {
    anErrorHappened: 'Ein Fehler ist aufgetreten',
    badRequest: 'Ungültige Anfrage',
    cprLookupDailyLimitReached:
      'Das Tageslimit für CPR-Abfragen ist erreicht. Versuchen Sie es morgen erneut oder wenden Sie sich an den Support.',
    cprLookupRequiresMitId:
      'Eine CPR-Abfrage erfordert eine Anmeldung mit MitID. Melden Sie sich ab und mit MitID erneut an.',
    errorOccurredWhileUpdating: 'Beim Aktualisieren ist ein Fehler aufgetreten',
    errorLoadingAvatar: 'Fehler beim Laden des Avatars',
    invalidId: 'Keine gültige Identifikation',
    newHealthRecord:
      'Beim Erstellen des neuen Patienten ist ein Fehler aufgetreten',
    newPatient: 'Beim Erstellen des neuen Patienten ist ein Fehler aufgetreten',
    patientConsent:
      'Beim Aktualisieren der Patienteneinwilligung ist ein Fehler aufgetreten',
    somthingWentWrongPleaseContact:
      'Etwas ist schiefgelaufen. Bitte starten Sie die App neu. Wenn das Problem weiterhin besteht, kontaktieren Sie bitte den Support.',
    thisIsEmbarassing: 'Es tut uns leid!',
    unauthorized: 'Nicht autorisiert',
  },
  fieldPlaceholders: {
    enterAdditionalDetails: 'Weitere Details hier eingeben...',
    enterAnswer: 'Antwort hier eingeben...',
    enterNote: 'Notiz hier eingeben...',
    enterOtherAgeOfLesion: 'Beschreibung hier eingeben...',
    enterOtherObjectiveFindingsHere: 'Weitere Befunde hier eingeben...',
    enterOtherRiskFactorsHere: 'Weitere Risikofaktoren hier eingeben...',
    findADiagnosis: 'Diagnose finden',
    socialSecurityDummy: 'XXXzXXX-XXXX',
    typeHereToEnterDescription: 'Beschreibung hier eingeben...',
    typeHereToEnterOtherAge: 'Anderes Alter hier eingeben...',
    typeHereToEnterOtherChanges: 'Andere Veränderung(en) hier eingeben...',
    typeHereToEnterOtherInspections: 'Andere Kriterien hier eingeben...',
    typeHereToEnterOtherSymptoms: 'Weitere Symptome hier eingeben...',
    typeHereToEnterOtherTreatmentsOrTrauma:
      'Weitere Behandlungen hier eingeben...',
  },
  formOptions: {
    imageLocation: {
      closeUpImage: 'Nahaufnahme',
      dermoscopicImage: 'Dermatoskopisches Bild',
      location: 'Lokalisation (3D-Avatar)',
      overviewImage: 'Übersichtsbild',
    },
    reasonForConsult: {
      diagnosis: 'Hilfe bei der Diagnose',
      diagnosisAndManagement: 'Beides',
      management: 'Hilfe bei der geeigneten Behandlung',
    },
  },
  genders: {
    female: 'Weiblich',
    gender: 'Geschlecht',
    male: 'Männlich',
    other: 'Divers',
  },
  medicalRecord: {
    emptyPlaceholder: 'Abgeschlossene Registrierungen werden hier angezeigt',
    error: {
      editingMedicalRecord: 'Fehler beim Bearbeiten der Patientenakte',
    },
    fieldName: {
      aggravatingFactor: 'Verschlimmernd{{nr:er|e}} Faktor{{nr:en}}',
      alleviatingFactor: 'Lindernd{{nr:er|e}}  Faktor{{nr:en}}',
      assessmentDifficulty: 'Diagnoseschwierigkeit',
      breslowThickness: 'Breslow-Tiefe',
      changesInAppearance: 'Veränderung{{nr:en}} im Erscheinungsbild',
      changesMadeBy: 'Geändert von',
      clinicalAction: 'Klinische Handlung',
      clinicalDiagnosis: 'Klinische Diagnose',
      clinicalQuestion: 'Klinische Fragestellung',
      contactedBy: 'Bewertet von',
      diagnosis: 'Diagnose',
      pathologyID: 'Proben-ID',
      differentialDiagnosis: 'Differenzialdiagnose',
      disposition: 'Disposition',
      evaluatedBy: 'Beurteilt von',
      familyHistoryOfMelanoma: 'Familiäre Anamnese eines Melanoms',
      generalSymptom: 'Allgemein{{nr:es|e}} Symptom{{nr:e}}',
      hasFormerSkinCancer: 'Frühere maligne Melanome oder Hautkrebs',
      imageQuality: 'Bildqualität',
      immunosuppressantUse: 'Einnahme von Immunsuppressiva',
      lesionAge: 'Bestehen der Läsion',
      lesionThickness: 'Geschätzte Dicke',
      medicationOrExposure: 'Medikation oder Exposition',
      note: 'Weitere Informationen',
      patientResponseNote: 'Antwort des Patienten',
      pcpAssessmentReviewNote: 'Notiz',
      teleAssessmentNote: 'Notiz',
      otherObjectiveFindings:
        'Objektive Befunde (z. B. Sonnenschäden, multiple/besondere Nävi-Typen)',
      pregnancyWithin6Months: 'Schwangerschaft innerhalb der letzten 6 Monate',
      previousTreatmentOrTrauma: 'Frühere Behandlung oder Trauma',
      primaryDiagnosis: 'Primärdiagnose',
      rashAge: 'Bestehen des Ausschlags',
      recommendedClinicalAction: 'Empfohlene klinische Handlung',
      registeredBy: 'Registriert',
      created: 'Erstellt',
      riskFactors:
        'Risikofaktoren (z. B. Sonneneinwirkung, immunsuppressive Therapie, Familienanamnese)',
      secondaryDiagnosis: 'Sekundärdiagnose',
      skinType: 'Hauttyp',
      symptom: 'Symptom{{nr:e}}',
      tentativeClinicalDiagnosis: 'Verdachtsdiagnose',
      tertiaryDiagnosis: 'Tertiärdiagnose',
      timeSpentReviewing: 'Aufgewendete Zeit für Überprüfung',
      followUpPriority: 'Nachverfolgungspriorität',
      formAnswers: 'Formularantworten',
    },
    fieldValue: {
      age: '(Zeit: {age})',
      appearanceChanges: '{triageInfo}',
      changedFromOldToNew: 'Geändert von {old} zu {new}',
      closeUpImage: 'Nahaufnahme',
      dermoscopicImage: 'Dermatoskopisches Bild',
      none: 'Keine',
      notAskFurther: 'Nicht angegeben',
      notDisplayable: 'Konnte nicht angezeigt werden',
      overviewImage: 'Übersichtsbild',
      photosChanged: '{removed} entfernt, {added} hinzugefügt',
      timeSpentReviewingMinutes: '{minutes} Minute{{n}}',
      timeSpentIpcBillingParagraph:
        'I spent a total of {minutes} minute{{s}} on this IPC including review of patient data and records and documentation of my assessment. Consult was routed to referring provider.',
      unknownThickness: 'Tumordicke unbekannt (Tx)',
    },
    urgencyDescriptions: {
      none: 'Keine Empfehlung zur Nachverfolgung. Befunde ohne klinische Bedeutung oder ein bereits gut behandelter Zustand.',
      routine:
        'Nachverfolgung empfohlen, ohne Dringlichkeit. Z. B. gutartige Veränderungen oder Routinekontrollen, bei denen kein dringendes Handeln erforderlich ist.',
      subAcute:
        'Verdacht auf Basalzell- oder Plattenepithelkarzinom oder eine andere Läsion, bei der eine kurzfristige Nachverfolgung aufgrund des Behandlungsbedarfs als notwendig erachtet wird.',
      acute:
        'Offensichtliches malignes Melanom oder eine andere schwerwiegende Hautläsion, bei der eine dringende Beurteilung als notwendig erachtet wird.',
    },
    notRegistered: 'Nicht registriert',
    subtitle: {
      anamnesis: 'Anamnese',
      assessment: 'Beurteilung',
      baseline: 'Ausgangswert',
      dermatologicalDiagnosis: 'Neue dermatologische Einschätzung',
      histopathologicalAssessment: 'Histopathologische Einschätzung',
      histoPathologicalDiagnosis: 'Neue histopathologische Einschätzung',
      objective: 'Objektiv',
      patientContacted: 'Die Antwort des Spezialisten wurde überprüft',
      conditionTypeInfoEdited: '{type} wurde bearbeitet',
      teledermatologicalAssessment: 'Dermatologische Einschätzung',
      teleAssessment: 'Tele-Einschätzung',
      followUp: '{months} Monate Verlaufsuntersuchung',
      eConsult: 'eKonsil',
    },
    title: {
      history: 'Verlauf',
      latestClinicalInformations: 'Neueste klinische Informationen',
      medicalRecord: 'Krankenakte',
      planAndConclusion: 'Plan und Fazit',
      predispositions: 'Prädispositionen',
      latestDiagnosis: 'Neueste Diagnose',
      lesionInformation: 'Information zur Läsion',
      rashInformation: 'Information zum Ausschlag',
      patientInformation: 'Patienteninformation',
    },
    by: 'von',
  },
  medicalRecordTitle: {
    baseline: 'Ausgangswert',
    histopatologicalAssessment: 'Histopathologische Einschätzung',
    patientContacted: 'Die Antwort des Spezialisten wurde überprüft',
    conditionTypeInfoEdited: '{type} wurde bearbeitet',
    teledermatologicalAssessment: 'Dermatologische Einschätzung',
    patientInfoEdited: 'Patienteninformationen bearbeitet',
    clinicalActionRemoved: '{action} entfernt',
    clinicalActionCancelled: '{action} storniert',
  },
  medicalRecordValues: {
    age: '(Zeit: {age})',
    aggravatingFactor: 'Verschlimmernd{{nr:er|e}} Faktor{{nr:en}}',
    alleviatingFactor: 'Lindernde{{nr:er|e}}  Faktor{{nr:en}}',
    anamnesis: 'Anamnese',
    appearanceChanges: '{triageInfo}',
    assessmentDifficulty: 'Schwierigkeit',
    changedFromOldToNew: 'Geändert von {old} zu {new}',
    changesInAppearance: 'Veränderung{{nr:en}} im Erscheinungsbild',
    changesMadeBy: 'Geändert von',
    clinicalAction: 'Klinische Handlung',
    clinicalDiagnosis: 'Klinische Diagnose',
    closeUpImage: 'Nahaufnahme',
    contactedBy: 'Bewertet von',
    dermoscopicImage: 'Dermatoskopisches Bild',
    differentialDiagnosis: 'Differenzialdiagnose',
    evaluatedBy: 'Beurteilt von',
    familyHistoryOfMelanoma: 'Melanom in Familienanamnese',
    generalSymptom: 'Allgemein{{nr:es|e}} Symptom{{nr:e}}',
    hasFormerSkinCancer: 'Früheres malignes Melanom oder Hautkrebs',
    history: 'Verlauf',
    imageQuality: 'Bildqualität',
    immunosuppressantUse: 'Einnahme von Immunsuppressiva',
    latestClinicalInformations: 'Neueste klinische Informationen',
    lesionAge: 'Alter der Läsion',
    location: 'Lokalisation',
    medicalRecord: 'Patientenakte',
    medicationOrExposure: 'Medikation oder Exposition',
    none: 'Keine',
    notAskFurther: 'Nicht angegeben',
    note: 'Notiz',
    notRegistered: 'Nicht registriert',
    objective: 'Objektiv',
    otherObjectiveFindings: 'Weitere objektive Befunde',
    overviewImage: 'Übersichtsbild',
    planAndConclusion: 'Plan und Fazit',
    predispositions: 'Prädispositionen',
    previousTreatmentOrTrauma: 'Frühere Behandlung',
    primaryDiagnosis: 'Primärdiagnose',
    recommendedClinicalAction: 'Empfohlene klinische Handlung',
    riskFactors: 'Risikofaktoren',
    secondaryDiagnosis: 'Sekundärdiagnose',
    skinType: 'Hauttyp',
    symptom: 'Symptom{{nr:e}}',
    tentativeClinicalDiagnosis: 'Klinische Verdachtsdiagnose',
    tertiaryDiagnosis: 'Tertiärdiagnose',
    timeSpentReviewing: 'Aufgewendete Zeit für Überprüfung',
  },
  medicalUnitPicker: {
    noAccess: {
      logout: 'Abmelden',
      support: 'Melatech-Support:',
      title: 'Willkommen bei Dermloop!',
      gainAccess:
        'Um Zugang zu erhalten, wenden Sie sich bitte an Ihren Superuser.',
      sendReactivationEmailAction: 'Reaktivierungs-E-Mail senden',
      superUserRestoreDescription:
        'Um den Zugang wiederherzustellen, wenden Sie sich bitte an Ihren Superuser:',
      selfReactivateRestoreDescription:
        'Um den Zugriff wiederherzustellen, reaktivieren Sie bitte Ihr Konto, indem Sie auf die Schaltfläche unten klicken.',
      userDeactivated: 'Ihr Zugang (<>{email}<>) wurde deaktiviert.',
      userHasNoAccess:
        'Ihr Account (<>{email}<>) hat keinen Zugriff auf einen Standort.',
      userInactive:
        'Ihr Zugang (<>{email}<>) wurde aufgrund von Inaktivität über 90 Tage deaktiviert.',
    },
  },
  numberInput: {
    rangeHint: 'Geben Sie eine ganze Zahl von {min} bis {max} ein',
    minHint: 'Geben Sie eine ganze Zahl von mindestens {min} ein',
    maxHint: 'Geben Sie eine ganze Zahl von höchstens {max} ein',
    wholeHint: 'Geben Sie eine ganze Zahl ein',
  },
  patientInfo: {
    accNo: 'Patienten-ID.',
    cpr: 'CPR',
    phone: 'Tel.',
  },
  pendingInvitations: {
    alertBodyText:
      'Bitte öffnen Sie die Einladung in Ihrer E-Mail und klicken Sie auf den Link, um die Einladung anzunehmen.',
    alertClose: 'Schließen',
    alertTitle: 'Sie haben ausstehende Einladungen',
  },
  plaintext: {
    bad: 'Schlecht',
    baseline: 'Ausgangswert',
    biopsyClinicalActionSelectionOnlyOnMobile:
      'Biopsie kann als klinische Handlung<br/>NUR über Dermloop Capture ausgewählt werden',
    changeIn: 'Änderung in',
    clinicalActionRequiresImages:
      'Diese Handlung kann erst ausgewählt werden, wenn die benötigten Bilder aufgenommen wurden.',
    contactMelatechMail: '{email}',
    contactSupport: 'Support kontaktieren:',
    usCountryCode: '+1',
    euCountryCode: '+45',
    usPhoneLink: 'tel:+18336902903',
    euPhoneLink: 'tel:+4544408245',
    usSupportNumber: '(833) 690-2903',
    euSupportNumber: '44 40 82 45',
    dateOfBirth: 'Geburtsdatum',
    downloadImage: 'Herunterladen',
    enter: 'Eingeben',
    entered: 'Eingegeben',
    formerTreatmentOrTrauma: 'Vorherige Behandlung oder Trauma',
    good: 'Gut',
    invalid: 'Ungültig',
    mandatory: 'Pflichtfeld',
    no: 'Nein',
    not: 'Nicht',
    nrFormerTreatmenstOrTraumas: '{nr} frühere Behandlung{{en}} ausgewählt',
    nrObjectiveFindings: '{nr} objektiver Befund{{e}} ausgewählt',
    nrRiskFactors: '{nr} Risikofaktor{{en}} ausgewählt',
    okay: 'Okay',
    or: 'Oder',
    orSignIn: 'oder melden Sie sich mit Ihrem Benutzernamen und Passwort an',
    overTheLast: 'in den letzten',
    requestDeletion: 'Sie haben die Löschung Ihres Accounts beantragt am',
    scanCode:
      'Scannen Sie diesen Code mit Ihrem Handy, um den Code zu bestätigen:',
    scanned: 'Gescannt',
    seeMore: 'Mehr anzeigen',
    settings: 'Einstellungen',
    sinceUnknow: 'seit unbekannt',
    size: 'Größe',
    unknown: 'Unbekannt',
    unspecified: 'Nicht angegeben',
    while: 'während',
    years: 'Jahre',
    yes: 'Ja',
    yetBeenDeleted:
      'noch nicht gelöscht. Sie können daher Ihren Account mit allen Daten wiederherstellen.',
    yourAccountHas: 'Ihr Account wurde',
  },
  restoreUserRequest: {
    accountNotDeleted:
      'Ihr Account wurde <>NICHT<> gelöscht. Sie können ihn daher mit allen Daten wiederherstellen.',
    actionText: {
      logOut: 'Abmelden',
      restore: 'Wiederherstellen',
    },
    requestDate:
      'Sie haben am <>{date}<> die Löschung Ihres Accounts beantragt.',
    title: 'Account wiederherstellen?',
  },
  roles: {
    admin: 'Dermloop-Admin',
    editor: 'Bearbeiter',
    jurisdictionalGroupInsuranceManager: 'Versicherungsmanager',
    learnUser: 'Learn-Nutzer',
    manager: 'Manager',
    medicalUnitManager: 'Superuser',
    medicalUnitParticipant: 'Kliniker',
    medicalUnitAssistant: 'Medizinischer Assistent',
    medicalUnitPathologist: 'Pathologe',
    medicalUnitTeledermatologist: 'Dermatologe',
    medicalUnitTeledermatologistManager: 'Leitender Dermatologe',
    researcher: 'Forscher',
    reviewer: 'Gutachter',
    siteManager: 'Standortleiter',
    trainingAssessmentExporter: 'Learn-Datenexporteur',
  },
  conditionTypes: {
    lesionExamples: 'Naevus, Hautkrebs, etc.',
  },
  statuses: {
    diagnosed: 'Diagnostiziert',
    draft: 'Entwurf',
    noAdditionalImages: 'Keine zusätzlichen Bilder',
    noRegisteredDiagnosis: 'Keine registrierte Diagnose',
    nrCloseUpImages: '{nr} Nahmaufnahme{{n}}',
    nrDermoscopicImages: '{nr} dermatoskopisch{{nr:es|e}} Bild{{er}}',
    nrOverviewImages: '{nr} Übersichtsbild{{er}}',
    overdueFollowup: 'Überfällige Nachsorge',
    pendingDiagnostic: 'Ausstehende Diagnose',
    undiagnosable: 'Nicht diagnostizierbar',
  },
  statusMessages: {
    invalidSSN: 'Ungültige CPR-Nummer',
    signedInAs: 'Sie sind angemeldet als:',
    switchAccount: 'Benutzer wechseln',
    unsavedChanges: 'Nicht gespeicherte Änderungen',
    welcomeBack: 'Willkommen zurück',
  },
  tooltips: {
    selectMultiple: 'Mehrfachauswahl',
  },
  email: {
    linkToDermloop: 'Zu Dermloop',
    unitBusinessHours: 'Geschäftsstunde{{hours:n}}',
    unitHours: 'Stunde{{hours:n}}',
    preReassignmentWarning: {
      titleOne: 'Fall wird bald weitergeleitet',
      titleMany: 'Fälle werden bald weitergeleitet',
      bodyOneSoon:
        'Wir haben festgestellt, dass Sie 1 unerledigten Fall haben. Er wird in weniger als {hours} {unit} weitergeleitet. Klicken Sie auf die Schaltfläche unten, um zu Ihren Fällen zu gelangen.',
      bodyManySoon:
        'Wir haben festgestellt, dass Sie {count} unerledigte Fälle haben. Der erste wird in weniger als {hours} {unit} weitergeleitet. Klicken Sie auf die Schaltfläche unten, um zu Ihren Fällen zu gelangen.',
      bodyOneImminent:
        'Wir haben festgestellt, dass Sie 1 unerledigten Fall haben. Er wird unmittelbar weitergeleitet. Klicken Sie auf die Schaltfläche unten, um zu Ihren Fällen zu gelangen.',
      bodyManyImminent:
        'Wir haben festgestellt, dass Sie {count} unerledigte Fälle haben. Der erste wird unmittelbar weitergeleitet. Klicken Sie auf die Schaltfläche unten, um zu Ihren Fällen zu gelangen.',
    },
    reassignmentAlert: {
      title: '{count} {{count:Fall|Fälle}} in {federationName} weitergeleitet',
      body: '{count} {{count:Fall|Fälle}} {{count:wurde|wurden}} seit {cutoff} weitergeleitet.',
      sendersHeading: 'Zuvor zugewiesen an:',
      senderCaseCount: '{count} {{count:Fall|Fälle}}',
      senderOverflow: 'und {count} {{count:weiterer|weitere}}',
      senderUnknown: 'Unbekannter Absender',
    },
  },
  sms: {
    patientResponseReady:
      'Ihre Antwort auf Ihre dermatologische Beurteilung auf Dermloop ist jetzt bereit. Klicken Sie auf den Link unten, um Ihre Antwort einzusehen. Sie werden gebeten, Ihre Identität mit Ihrer Telefonnummer zu verifizieren. Der Link ist 30 Tage lang aktiv. Bei Fragen wenden Sie sich bitte telefonisch an NurseCare unter +45 70 20 22 32 oder per E-Mail an info@nursecare.dk',
  },
  warningMessages: {
    delete: 'Löschen',
    discard: 'Verwerfen',
    leaveWithoutSaving: 'Ohne Speichern verlassen',
    leaveWithoutSavingWarning:
      'Sie sind dabei, diese Seite zu verlassen. Möchten Sie wirklich ohne Speichern fortfahren?',
  },
} satisfies NamespaceSharedTranslation;

export default de_shared;
